import { useState } from 'react';
import { Button } from 'react-bootstrap';
import { listDeliveryPayables, remindDeliveryPayableOwner, resolveDeliveryPayable } from '../api/financeApi';
import useFinanceQuery from '../useFinanceQuery';
import FinanceListPage from '../components/FinanceListPage';
import DetailLinkButton from '../components/DetailLinkButton';
import MarkPaymentModal from '../components/MarkPaymentModal';
import ConfirmModal from '../components/ConfirmModal';
import MessageModal from '../components/MessageModal';
import { useFinance } from '../financeContext';
import { legLabel } from '../wording';
import { money } from '../format';
import { StatusBadge, OverdueBadge, remindState } from './rowBits';

const STATUS_OPTIONS = [
  { value: 'escalated', label: 'Escalated' },
  { value: 'pending', label: 'Pending' },
  { value: 'paid', label: 'Paid' },
  { value: 'rejected', label: 'Rejected' }
];

export default function DeliveryPaymentsPage() {
  const { canApproveFinance, isAdmin, vertical, wording } = useFinance();
  const q = useFinanceQuery(
    (params) => listDeliveryPayables({ status: 'escalated', ...params }),
    { filterKeys: ['status', 'vertical', 'overdue', 'hasReminders', 'minAmount', 'maxAmount', 'dateFrom', 'dateTo', 'ownerId'] }
  );
  const [payTarget, setPayTarget] = useState(null);
  const [busyId, setBusyId] = useState('');
  const [actionError, setActionError] = useState('');
  const [confirm, setConfirm] = useState(null);

  const act = async (id, action) => {
    setBusyId(id);
    try {
      if (action === 'remind') await remindDeliveryPayableOwner(id);
      else await resolveDeliveryPayable(id, { action });
      await q.reload();
    } catch (err) {
      setActionError(err?.response?.data?.msg || 'Unable to update that payment.');
    } finally {
      setBusyId('');
    }
  };

  const columns = [
    { key: 'ref', header: 'Order', cardHeader: true, cell: (r) => <DetailLinkButton kind="booking" entity={r} label={`#${r.bookingRef || (r.bookingId || '').slice(-6)}`} /> },
    { key: 'person', header: wording.deliveryPerson, cell: (r) => (
      r.deliveryPersonId
        ? <DetailLinkButton kind="staff" id={r.deliveryPersonId} label={r.deliveryPersonName || '—'} />
        : (r.deliveryPersonName || '—')
    ) },
    { key: 'owner', header: 'Owner', cell: (r) => (
      <span>{r.ownerName || '—'}<div className="small text-muted">{[r.ownerEmail, r.ownerPhone].filter(Boolean).join(' · ')}</div></span>
    ) },
    { key: 'leg', header: 'For', cell: (r) => legLabel(vertical, r.leg) },
    { key: 'amount', header: 'Amount', cell: (r) => money(r.amount) },
    { key: 'status', header: 'Status', cell: (r) => (
      <StatusBadge status={r.status} extra={<><OverdueBadge row={r} />{r.escalationNote && <div className="small text-muted">"{r.escalationNote}"</div>}</>} />
    ) }
  ];

  const rowActions = !canApproveFinance ? undefined : (r) => {
    if (!['pending', 'escalated'].includes(r.status)) {
      return <span className="small text-muted">{r.reviewerNote || '—'}</span>;
    }
    const rs = remindState(r);
    return (
      <div className="d-flex flex-wrap gap-1">
        {rs.show && (
          <Button size="sm" variant="outline-secondary" disabled={busyId === r._id} onClick={() => act(r._id, 'remind')}>Remind owner</Button>
        )}
        <Button size="sm" variant="success" onClick={() => setPayTarget({ kind: 'deliveryPayable', id: r._id, amount: r.amount, title: `#${r.bookingRef || ''}` })}>Mark paid</Button>
        <Button size="sm" variant="outline-danger" disabled={busyId === r._id} onClick={() => setConfirm({ id: r._id, action: 'reject' })}>Reject</Button>
        {rs.hint && <div className="w-100 small text-muted">{rs.hint}</div>}
      </div>
    );
  };

  return (
    <>
      <FinanceListPage
        title={wording.deliveryPayments}
        subtitle={`What business owners owe ${wording.deliveryPersonPlural.toLowerCase()} / agents per completed leg.`}
        q={q}
        columns={columns}
        rowActions={rowActions}
        filterFields={['status', 'vertical', 'overdue', 'hasReminders', 'amount', 'date', 'owner']}
        statusOptions={STATUS_OPTIONS}
        searchPlaceholder="Search order ref, owner or person"
        emptyText="Nothing here."
      />
      <MarkPaymentModal
        target={payTarget}
        show={!!payTarget}
        asAdmin={isAdmin}
        onHide={() => setPayTarget(null)}
        onRecorded={() => { setPayTarget(null); q.reload(); }}
      />
      <ConfirmModal
        show={!!confirm}
        onHide={() => setConfirm(null)}
        title="Reject this payment?"
        message="The owner will no longer owe this amount. This cannot be undone from here."
        confirmLabel="Reject"
        confirmVariant="danger"
        busy={!!busyId}
        onConfirm={async () => {
          if (!confirm) return;
          await act(confirm.id, confirm.action);
          setConfirm(null);
        }}
      />
      <MessageModal
        show={!!actionError}
        onHide={() => setActionError('')}
        title="Could not update payment"
        message={actionError}
      />
    </>
  );
}
